import { createColumnHelper } from "@tanstack/react-table";
import { useId, useMemo, useState } from "react";
import { Link } from "react-router";

import { deepLinks } from "../../navigation/deep-links";
import { areaPath } from "../../navigation/offering";
import { useAreaHref } from "../../navigation/use-offering";
import { AsyncState } from "../../ui/async-state";
import { Avatar } from "../../ui/avatar";
import { Button } from "../../ui/button";
import { DataTable } from "../../ui/data-table";
import { PageHeader } from "../../ui/page-header";
import { useMemoryCapabilities } from "../access";
import { nameWith, WarningNote } from "../components/parts";
import { describeMemoryError } from "../errors";
import { relationshipLabels } from "../labels";
import { CUSTOMER_LIST_MAX, CUSTOMER_LIST_PAGE, useCustomerList } from "../queries";

type CustomerRow = Readonly<{
  customerId: string;
  preferredName: string | null;
  relationship: keyof typeof relationshipLabels;
  /** The position of the row, for a screen reader: two customers can have no name, or the same one. */
  index: number;
  total: number;
}>;

const column = createColumnHelper<CustomerRow>();

/** The links of one row. Each is named by its own words and then the text of the row's name cell (no name in an attribute). */
function RowLinks({ row }: Readonly<{ row: CustomerRow }>) {
  const supportId = useId();
  const memoryId = useId();
  const nameId = `customer-name-${row.customerId}`;
  return (
    <div className="flex flex-wrap justify-end gap-2">
      <Button asChild variant="outline" size="sm">
        <Link
          id={supportId}
          to={deepLinks.customerSupport(row.customerId)}
          aria-labelledby={nameWith(supportId, nameId)}
        >
          Support view
        </Link>
      </Button>
      <Button asChild variant="outline" size="sm">
        <Link
          id={memoryId}
          to={deepLinks.customerMemory(row.customerId)}
          aria-labelledby={nameWith(memoryId, nameId)}
        >
          Open memory
        </Link>
      </Button>
    </div>
  );
}

const columns = [
  column.accessor((row) => row.preferredName ?? "Name not set", {
    id: "name",
    header: "Customer",
    meta: {
      rowHeader: true,
      headClassName: "first:pl-[70px] compact:first:pl-[58px]",
    },
    cell: ({ row }) => (
      <span className="flex items-center gap-3.5">
        <Avatar
          id={row.original.customerId}
          name={row.original.preferredName ?? ""}
          size="md"
          className="compact:size-8 compact:text-xs"
        />
        <span
          id={`customer-name-${row.original.customerId}`}
          className="font-serif text-lg font-medium compact:text-base"
        >
          {row.original.preferredName ?? "Name not set"}
          <span className="sr-only">
            , customer {row.original.index} of {row.original.total}
          </span>
        </span>
      </span>
    ),
  }),
  column.accessor((row) => relationshipLabels[row.relationship], {
    id: "relationship",
    header: "Your relationship",
    meta: { className: "md:w-[220px]" },
  }),
  column.display({
    id: "open",
    header: () => <span className="sr-only">Open</span>,
    meta: { label: "Open", className: "md:w-[284px]" },
    cell: ({ row }) => <RowLinks row={row.original} />,
  }),
];

/**
 * M-S1. The customers a staff member may open the memory of: the assigned customers of a concierge, every
 * customer for a supervisor. The row holds a preferred name and the staff member's own relationship, and
 * nothing else of the profile. The API decides who appears; this page lists what it is given.
 */
export function CustomerListPage() {
  const { capabilities, isLoading: rolesLoading } = useMemoryCapabilities();
  // The Privacy area is its own area (its own roles): the button is offered only to a role that opens it.
  const privacyHref = useAreaHref()("privacy", areaPath("privacy"));
  const [limit, setLimit] = useState(CUSTOMER_LIST_PAGE);
  const list = useCustomerList(limit, capabilities.listCustomers);
  const customers = list.data?.customers;
  const rows: readonly CustomerRow[] = useMemo(
    () =>
      (customers ?? []).map((customer, index, all) => ({
        ...customer,
        index: index + 1,
        total: all.length,
      })),
    [customers],
  );

  if (rolesLoading) {
    return (
      <AsyncState kind="loading" title="Checking your access" message="Reading your staff role." />
    );
  }
  if (!capabilities.listCustomers) {
    return (
      <AsyncState
        kind="error"
        title="Not available for your role"
        message="Your staff role does not include customer memory."
      />
    );
  }

  return (
    <section className="grid grid-cols-[minmax(0,1fr)] gap-6" aria-labelledby="page-title">
      <PageHeader
        eyebrow="Lafayette operations"
        title="Customers"
        description="Open a customer to read and manage what Lafayette remembers about them."
        actions={
          privacyHref === null ? null : (
            <Button asChild variant="outline">
              <Link to={privacyHref}>Privacy requests</Link>
            </Button>
          )
        }
      />

      {list.isPending ? (
        <AsyncState kind="loading" title="Loading customers" message="Waiting for the answer." />
      ) : list.isError ? (
        <ListError error={list.error} onRetry={() => void list.refetch()} />
      ) : list.data.customers.length === 0 ? (
        <AsyncState
          kind="empty"
          title="No customers to show"
          message="No customer is assigned to you yet. A supervisor can assign customers."
        />
      ) : (
        <div className="grid gap-4" aria-busy={list.isFetching}>
          <DataTable
            columns={columns}
            data={rows}
            getRowId={(row) => row.customerId}
            caption="Customers you may open"
            stickyHeader={false}
            className="rounded-lg border-hairline p-2"
            rowClassName={() => "h-17 border-hairline compact:h-12"}
          />
          {list.data.truncated ? (
            <WarningNote>
              More customers exist than this list shows.{" "}
              {limit < CUSTOMER_LIST_MAX ? (
                <Button
                  variant="link"
                  size="sm"
                  className="px-0 align-baseline"
                  onClick={() => setLimit(CUSTOMER_LIST_MAX)}
                >
                  Show up to {CUSTOMER_LIST_MAX}
                </Button>
              ) : (
                <>The list stops at {CUSTOMER_LIST_MAX} customers.</>
              )}
            </WarningNote>
          ) : null}
        </div>
      )}
    </section>
  );
}

function ListError({ error, onRetry }: Readonly<{ error: unknown; onRetry: () => void }>) {
  const failure = describeMemoryError(error);
  if (failure.kind === "forbidden") {
    return <AsyncState kind="error" title="You cannot open this list" message={failure.message} />;
  }
  return (
    <AsyncState
      kind="error"
      title="The customer list did not load"
      message={failure.message}
      onRetry={onRetry}
    />
  );
}
